import axios from "axios";

/* ========================================================================== */
/* 🌐 Base Configuration                                                      */
/* ========================================================================== */

const API_URL =
  process.env.REACT_APP_API_URL || "http://localhost:8080/api";

if (!process.env.REACT_APP_API_URL) {
  console.warn("⚠️ REACT_APP_API_URL not set. Using localhost fallback.");
}

const apiClient = axios.create({
  baseURL: API_URL,
  timeout: 15000,
  headers: {
    "Content-Type": "application/json",
  },
});

/* ========================================================================== */
/* 🔐 REQUEST INTERCEPTOR                                                     */
/* Attach token + company context                                             */
/* ========================================================================== */

apiClient.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem("token");
    const companyId = localStorage.getItem("companyId");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    if (companyId) {
      config.headers["X-Company-Id"] = companyId;
    } else {
      console.warn("⚠️ Missing X-Company-Id header");
    }
    // 🧪 Debug helper (dev only)
    if (process.env.NODE_ENV === "development") {
      config.headers["X-Debug-Company"] = companyId || "none";
    }
    return config;
  },
  (error) => Promise.reject(error)
);

/* ========================================================================== */
/* ⚠️ RESPONSE INTERCEPTOR                                                    */
/* Global error handling + session control                                    */
/* ========================================================================== */

apiClient.interceptors.response.use(
  (response) => response,

  (error) => {
    const { response } = error;

    /* ---------------- 401: Session Expired ---------------- */
    if (response?.status === 401) {
      console.warn("🔒 Session expired. Logging out...");

      clearAuthSession();

      // Prevent redirect loop
      if (!window.location.pathname.includes("/login")) {
        window.location.href = "/login";
      }
    }

    /* ---------------- Normalize Error ---------------- */
    const normalizedError = {
      status: response?.status || 500,
      message:
        response?.data?.message ||
        response?.data?.error ||
        error.message ||
        "Unexpected error occurred",
      data: response?.data || {},
      original: error, // preserve original error for debugging
    };

    return Promise.reject(normalizedError);
  }
);

/* ========================================================================== */
/* 🔄 MULTI-TAB SESSION SYNC                                                  */
/* If user logs out in one tab → all tabs logout                              */
/* ========================================================================== */

if (typeof window !== "undefined") {
  window.addEventListener("storage", (event) => {
    if (event.key === "token" && !event.newValue) {
      window.location.href = "/login";
    }
  });
}

/* ========================================================================== */
/* 🧠 AUTH + COMPANY HELPERS                                                  */
/* ========================================================================== */

export const setAuthSession = (token) => {
  if (!token) return;
  localStorage.setItem("token", token);
};

export const clearAuthSession = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("companyId");
};

export const setCompanyContext = (companyId) => {
  if (!companyId) return;
  localStorage.setItem("companyId", companyId);
};

export const clearCompanyContext = () => {
  localStorage.removeItem("companyId");
};

/* ========================================================================== */
/* 🧪 DEBUG (DEV ONLY)                                                        */
/* ========================================================================== */

export const debugApiConfig = () => {
  if (process.env.NODE_ENV === "development") {
    console.log("🌐 API Base URL:", API_URL);
    console.log("🔑 Token:", localStorage.getItem("token"));
    console.log("🏢 Company:", localStorage.getItem("companyId"));
  }
};

/* ========================================================================== */
/* 📦 EXPORT                                                                  */
/* ========================================================================== */

export default apiClient;